import { AfterViewInit, ChangeDetectorRef, Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ApexAxisChartSeries, ApexChart, ApexFill, ApexGrid, ApexPlotOptions, ApexStroke, ApexTooltip, ApexXAxis, ApexYAxis, ChartComponent, NgApexchartsModule } from 'ng-apexcharts';
import { Utils, ObjectUtils, DocumentHelper, DbDocument } from 'fakturnia-shared';
import { DateTime } from 'luxon';
import { Subscription } from 'rxjs';
import { DataSynchronizerService } from 'shared/services/data-synchronization.service';
import { DocumentsService } from 'shared/services/documents.service';
import { MonthYearInvoices } from '../invoice-card/invoice-card.component';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatMenuModule } from '@angular/material/menu';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatDatepickerInputEvent, MatDatepickerModule } from '@angular/material/datepicker';
import { MatLuxonDateModule } from '@angular/material-luxon-adapter';
import { MatTooltipModule } from '@angular/material/tooltip';
import { FormsModule } from '@angular/forms';
import { SchemeService } from 'shared/services/scheme.service';

interface ChartDataPoint {
  x: number;
  y: number;
}

interface ChartSeries {
  name: string;
  category: 'income' | 'expense';  // Niestandardowa klasyfikacja (nie ApexCharts type!)
  data: ChartDataPoint[];
}

@Component({
  selector: 'income-chart',
  standalone: true,
  imports: [CommonModule, NgApexchartsModule, FormsModule, MatTooltipModule, MatIconModule, MatButtonModule, MatFormFieldModule, MatInputModule, MatDatepickerModule, MatLuxonDateModule, MatMenuModule],
  templateUrl: './income-chart.component.html',
  styleUrl: './income-chart.component.scss'
})
export class IncomeChartComponent implements OnInit, AfterViewInit, OnDestroy {

  @ViewChild('chartRef') chartRef: ChartComponent;

  private _subscriptions: Subscription[] = [];

  public viewMode: 'monthly' | 'daily' = 'daily';
  public selectedRange: 'THIS_MONTH' | 'LAST_30_DAYS' | 'THIS_YEAR' | 'LAST_12_MONTHS' | null = null

  public startDate: DateTime = DateTime.now().minus({ months: 11 }); // Default: 12 months ago
  public endDate: DateTime = DateTime.now(); // Default: Current date

  public chartOptions: {
    series: ApexAxisChartSeries;
    chart: ApexChart;
    xaxis: ApexXAxis;
    dataLabels: any;
    stroke: ApexStroke;
    grid: ApexGrid;
    fill: ApexFill;
    tooltip: ApexTooltip;
    yaxis: ApexYAxis;
    colors: string[];
    legend: any;
    markers: any;
    plotOptions: ApexPlotOptions;
  };

  countFieldOptions: { value: 'vatSum' | 'netSum' | 'grossSum', text: string }[] = [
    { value: 'netSum', text: 'Wartość netto' },
    { value: 'vatSum', text: 'Kwota VAT' },
    { value: 'grossSum', text: 'Wartość brutto' },
  ];

  selectedField = this.countFieldOptions[2];
  data: MonthYearInvoices[] = [];
  expenseData: MonthYearInvoices[] = [];

  chartData: ChartSeries[] = []

  summaries: any = []
  expenseSummaries: any = []
  visibleDataText = ''

  docsCount = 0
  scheme = null

  chartBackgroundGradient = ''
  chartLabelColors = ''

  constructor(
    private _dataSynchronizerService: DataSynchronizerService,
    private _documentsService: DocumentsService,
    private _schemeService: SchemeService,
    private _cdr: ChangeDetectorRef
  ) { }

  ngAfterViewInit(): void {
    this._subscriptions.push(
      this._dataSynchronizerService.currentData.subscribe({
        next: (data) => {

          if (!data) return
          if (data.table == 'documents') {
            this._loadData()
          }
        }
      })
    )
    // docsCount jest aktualizowany w _loadData()
  }

  public setThisMonthRange() {
    this.selectedRange = 'THIS_MONTH'
    this.viewMode = 'daily'
    this.startDate = DateTime.now().startOf('month')
    this.endDate = DateTime.now().endOf('month')
    this._loadData()
  }

  public setLast30DaysRange() {
    this.selectedRange = 'LAST_30_DAYS'
    this.viewMode = 'daily'
    this.startDate = DateTime.now().minus({ days: 30 }).startOf('day')
    this.endDate = DateTime.now().endOf('day')
    this._loadData()
  }

  public setThisYearRange() {
    this.selectedRange = 'THIS_YEAR'
    this.viewMode = 'monthly'
    this.startDate = DateTime.now().startOf('year')
    this.endDate = DateTime.now().endOf('year')
    this._loadData()
  }

  public setLastMonthsRange(months) {
    this.selectedRange = 'LAST_12_MONTHS'
    this.viewMode = 'monthly'
    this.startDate = DateTime.now().minus({ months: 12 }).startOf('month')
    this.endDate = DateTime.now().endOf('month')
    this._loadData()
  }

  rangeChanged(startOrEnd: string, event: MatDatepickerInputEvent<any, any>) {

    this.selectedRange = null
    if (event.value == null) return
    // const date = new Date(event.value.ts)

    if (startOrEnd == 'start') {
      this.startDate = event.value
    }
    else if (startOrEnd == 'end') {
      if (event.value != null) {
        this.endDate = event.value
        if (this.startDate >= this.endDate) {
          this.endDate = event.value
        }
        this._loadData()
      }
    }


  }
  public toDecimal(value) {
    return Utils.toDecimal(value)
  }

  public selectField(field: { value: 'vatSum' | 'netSum' | 'grossSum'; text: string }): void {
    this.selectedField = field; // Update the selected field
    this._loadData(); // Reload the data based on the selected field
  }


  private _loadData() {
    this._buildRange();
    this._addInvoices();
    this._addExpenses();

    // Generate series for income (all currencies)
    let incomeCurrencies = this._getAllCurrencies(this.data);
    const expenseCurrencies = this._getAllCurrencies(this.expenseData);

    // Always include PLN for income
    if (!incomeCurrencies.includes('PLN')) {
      incomeCurrencies = ['PLN', ...incomeCurrencies];
    }

    const incomeSeries: ChartSeries[] = incomeCurrencies.map(currency => ({
      name: `Przychody ${currency}`,
      category: 'income' as const,
      data: this.data.map((monthYear: MonthYearInvoices) => {
        const summaryItem = monthYear.summary.find(s => s.currency === currency);
        const year = monthYear.year || 0;
        const month = monthYear.month || 1;
        const day = this.viewMode === 'daily' ? monthYear.day || 1 : 1;
        const date = DateTime.fromObject({ year, month, day });

        return {
          x: date.isValid ? date.toMillis() : DateTime.now().toMillis(),
          y: summaryItem && typeof summaryItem.summary === 'number' ? summaryItem.summary : 0
        };
      })
    }));

    const expenseSeries: ChartSeries[] = expenseCurrencies.map(currency => ({
      name: `Koszty ${currency}`,
      category: 'expense' as const,
      data: this.expenseData.map((monthYear: MonthYearInvoices) => {
        const summaryItem = monthYear.summary.find(s => s.currency === currency);
        const year = monthYear.year || 0;
        const month = monthYear.month || 1;
        const day = this.viewMode === 'daily' ? monthYear.day || 1 : 1;
        const date = DateTime.fromObject({ year, month, day });

        return {
          x: date.isValid ? date.toMillis() : DateTime.now().toMillis(),
          y: summaryItem && typeof summaryItem.summary === 'number' ? summaryItem.summary : 0
        };
      })
    }));

    this.chartData = [...incomeSeries, ...expenseSeries];
    this.chartOptions.series = this.chartData;
    this.chartOptions.colors = this._getChartColors();

    this._calculatePeriodSummaries();
    this.docsCount = this._documentsService.countIssuedDocuments();
    
    // Force chart update
    if (this.chartRef) {
      this.chartRef.updateSeries(this.chartData);
    }
    
    this._cdr.detectChanges();
  }

  private _getAllCurrencies(dataSource: MonthYearInvoices[]): string[] {
    const currencies = new Set<string>();
    dataSource.forEach(monthYear => {
      monthYear.summary.forEach(summary => currencies.add(summary.currency));
    });
    return Array.from(currencies);
  }

  public setViewMode(mode: 'daily' | 'monthly') {
    this.viewMode = mode
    this._loadData()
  }


  private _buildRange() {
    this.data = [];
    this.expenseData = [];

    let currentDate = this.startDate;

    if (this.viewMode === 'monthly') {
      // Monthly view: Group by month
      while (currentDate <= this.endDate) {
        this.data.push(
          new MonthYearInvoices({
            month: currentDate.month,
            year: currentDate.year,
            countFieldId: this.selectedField.value,
          })
        );
        this.expenseData.push(
          new MonthYearInvoices({
            month: currentDate.month,
            year: currentDate.year,
            countFieldId: this.selectedField.value,
          })
        );

        currentDate = currentDate.plus({ months: 1 }); // Move to the next month
      }
    } else if (this.viewMode === 'daily') {
      // Daily view: Group by day
      while (currentDate <= this.endDate) {
        this.data.push(
          new MonthYearInvoices({
            day: currentDate.day,
            month: currentDate.month,
            year: currentDate.year,
            countFieldId: this.selectedField.value,
          })
        );
        this.expenseData.push(
          new MonthYearInvoices({
            day: currentDate.day,
            month: currentDate.month,
            year: currentDate.year,
            countFieldId: this.selectedField.value,
          })
        );

        currentDate = currentDate.plus({ days: 1 }); // Move to the next day
      }
    }
  }

  private _addInvoices() {

    const invoiceTypes = [
      'Faktura',
      'FakturaWDT',
      'FakturaKoncowa',
      'FakturaKorygujaca',
      'FakturaKorygujacaZaliczkowa',
      'FakturaKorygujacaRozliczeniowa',
      'FakturaRozliczeniowa',
      'FakturaMPP',
      'FakturaVATMarza',
      'FakturaVAT',
      'FakturaZaliczka'
    ];

    this.data.forEach((element: MonthYearInvoices) => {
      let issuedInvoices: DbDocument[] = [];

      if (this.viewMode === 'monthly') {
        // Fetch invoices by month and year
        issuedInvoices = this._documentsService.getDocumentsByMonthAndYear(
          invoiceTypes,
          element.month,
          element.year
        );
      } else if (this.viewMode === 'daily' && element.day !== undefined) {
        // Fetch invoices by specific day, month, and year
        issuedInvoices = this._documentsService.getDocumentsByDayMonthAndYear(
          invoiceTypes,
          element.day,
          element.month,
          element.year
        );
      }

      // Filtruj tylko dokumenty z direction = 'income' (przychody, nie koszty)
      const incomeDocuments = issuedInvoices.filter((document: DbDocument) => 
        DocumentHelper.getDirection(document) === 'income'
      );

      // Add invoices to the element - używaj wartości z dokumentu bezpośrednio
      // aby uniknąć problemu z Invoice.count() resetującym wartości dla KSeF
      incomeDocuments.forEach((document: DbDocument) => {
        const value = this._getDocumentValue(document);
        const currency = this._getDocumentCurrency(document);
        
        element.addInvoice({
          _id: document._id,
          grossSum: this.selectedField.value === 'grossSum' ? value : 0,
          netSum: this.selectedField.value === 'netSum' ? value : 0,
          vatSum: this.selectedField.value === 'vatSum' ? value : 0,
          currency: currency,
          source: document.source,
          documentType: document.documentType,
          linkedOrder: document.linkedOrder,
          advancePaymentAmount: document.advancePaymentAmount,
          advancePaymentTaxBreakdown: document.advancePaymentTaxBreakdown
        });
      });
    });

  }

  private _addExpenses() {
    this.expenseData.forEach((element: MonthYearInvoices) => {
      let allDocuments: DbDocument[] = [];

      if (this.viewMode === 'monthly') {
        allDocuments = this._documentsService.getAllDocumentsByMonthAndYear(
          element.month,
          element.year
        );
      } else if (this.viewMode === 'daily' && element.day !== undefined) {
        allDocuments = this._documentsService.getAllDocumentsByDayMonthAndYear(
          element.day,
          element.month,
          element.year
        );
      }

      // Filtruj tylko dokumenty z direction = 'expense' (koszty)
      const expenseDocuments = allDocuments.filter((document: DbDocument) =>
        DocumentHelper.getDirection(document) === 'expense'
      );

      // Add expenses to the element - używamy wartości bezpośrednio z dokumentu
      expenseDocuments.forEach((document: DbDocument) => {
        // Dla kosztów z KSeF używamy grossSum bezpośrednio
        const expenseValue = this._getDocumentValue(document);
        const currency = this._getDocumentCurrency(document);
        
        // Tworzymy pseudo-invoice z potrzebnymi polami
        element.addInvoice({
          _id: document._id,
          grossSum: this.selectedField.value === 'grossSum' ? expenseValue : 0,
          netSum: this.selectedField.value === 'netSum' ? expenseValue : 0,
          vatSum: this.selectedField.value === 'vatSum' ? expenseValue : 0,
          currency: currency,
          source: document.source
        });
      });
    });
  }

  /**
   * Pobiera wartość dokumentu
   */
  private _getDocumentValue(document: DbDocument): number {
    const field = this.selectedField.value; // 'grossSum' | 'netSum' | 'vatSum'

    // Dla FakturaZaliczka z zamówieniem — kwota zaliczki, nie łączna wartość zamówienia
    // (grossSum w DB = wartość zamówienia, advancePaymentAmount = kwota zaliczki)
    if (document.documentType === 'FakturaZaliczka' && (document as any).linkedOrderId && (document as any).advancePaymentAmount !== undefined) {
      if (field === 'grossSum') return (document as any).advancePaymentAmount;
      const breakdown = (document as any).advancePaymentTaxBreakdown || [];
      if (field === 'netSum') return breakdown.reduce((sum: number, row: any) => sum + (row.netValue || 0), 0);
      if (field === 'vatSum') return breakdown.reduce((sum: number, row: any) => sum + (row.vatValue || 0), 0);
      return (document as any).advancePaymentAmount;
    }

    // Wartość z DB — działa dla wszystkich typów:
    // count() oblicza różnice dla korekt (FakturaKorygujaca, KOR_ZAL), kwoty dla zwykłych
    const value = (document as any)[field];
    if (value != null) return value;

    // Fallback: grossSum zawsze istnieje
    if (field === 'grossSum') return document.grossSum || 0;

    // Fallback: FakturaKorygujaca — oblicz różnicę z produktów
    if (document.documentType === 'FakturaKorygujaca') {
      return this._calculateCorrectionValue(document);
    }

    // Fallback: oblicz z produktów
    if (document.products?.length) {
      const productField = field === 'netSum' ? 'netValue' : 'amountOfVat';
      return document.products.reduce((sum: number, p: any) => sum + (p[productField] || 0), 0);
    }

    // Ostateczny fallback: proporcja z grossSum
    const gross = document.grossSum || 0;
    if (field === 'netSum') return gross / 1.23;
    return gross - gross / 1.23;
  }

  /**
   * Oblicza wartość korekty jako różnicę (po korekcie - przed korektą)
   */
  private _calculateCorrectionValue(document: DbDocument): number {
    const field = this.selectedField.value === 'grossSum' ? 'grossValue'
      : this.selectedField.value === 'netSum' ? 'netValue'
      : 'amountOfVat';

    let afterSum = 0;
    (document.products || []).forEach(product => {
      afterSum += product[field] || 0;
    });

    let beforeSum = 0;
    if (document.correctedInvoiceId) {
      const correctedDoc = this._documentsService.getById(document.correctedInvoiceId);
      if (correctedDoc?.products?.length) {
        correctedDoc.products.forEach(product => {
          beforeSum += product[field] || 0;
        });
      }
    }

    return afterSum - beforeSum;
  }

  /**
   * Pobiera walutę dokumentu
   */
  private _getDocumentCurrency(document: DbDocument): string {
    if (document.currency) {
      return document.currency;
    }
    const rawData = (document as any).ksefRawData;
    if (rawData?.currency) {
      return rawData.currency;
    }
    return 'PLN';
  }

  /**
   * Generuje kolory dla serii wykresu
   * Przychody = niebieski, Koszty = czerwony
   */
  private _getChartColors(): string[] {
    const colors: string[] = [];
    for (const series of this.chartData) {
      if (series.category === 'income') {
        colors.push('#10b981'); // zielony dla przychodów
      } else if (series.category === 'expense') {
        colors.push('#f43f5e'); // różowo-czerwony dla kosztów
      } else {
        colors.push('#6bbfff'); // domyślny
      }
    }
    return colors.length > 0 ? colors : ['#10b981'];
  }

  private _prepareChartOptions() {
    const isDark = this.scheme === 'dark';
    
    this.chartOptions = {
      series: [],
      chart: {
        defaultLocale: 'pl',
        locales: [{
          name: 'pl',
          options: {
            months: ['Styczeń', 'Luty', 'Marzec', 'Kwiecień', 'Maj', 'Czerwiec', 'Lipiec', 'Sierpień', 'Wrzesień', 'Październik', 'Listopad', 'Grudzień'],
            shortMonths: ['Sty', 'Lut', 'Mar', 'Kwi', 'Maj', 'Cze', 'Lip', 'Sie', 'Wrz', 'Paź', 'Lis', 'Gru'],
            days: ['Niedziela', 'Poniedziałek', 'Wtorek', 'Środa', 'Czwartek', 'Piątek', 'Sobota'],
            shortDays: ['ND', 'PN', 'WT', 'ŚR', 'CZ', 'PT', 'SB'],
            toolbar: {
              download: 'Pobierz SVG',
              selection: 'Zaznacz',
              selectionZoom: 'Powiększ zaznaczenie',
              zoomIn: 'Przybliż',
              zoomOut: 'Oddal',
              pan: 'Przesuń',
              reset: 'Resetuj',
            }
          }
        }],
        animations: {
          enabled: true,
          speed: 600,
          easing: 'easeinout',
          animateGradually: {
            enabled: true,
            delay: 100
          },
          dynamicAnimation: {
            enabled: true,
            speed: 350
          }
        },
        fontFamily: 'Inter, system-ui, sans-serif',
        foreColor: isDark ? '#94a3b8' : '#64748b',
        background: 'transparent',
        width: '100%',
        height: 280,
        type: 'bar',
        sparkline: {
          enabled: false
        },
        toolbar: {
          show: false
        },
        zoom: {
          enabled: false
        },
        dropShadow: {
          enabled: false
        }
      },
      colors: ['#10b981', '#f43f5e', '#3b82f6', '#f59e0b'],
      dataLabels: {
        enabled: false
      },
      fill: {
        type: 'solid',
        opacity: 0.85
      },
      grid: {
        show: true,
        borderColor: isDark ? '#334155' : '#e2e8f0',
        strokeDashArray: 4,
        padding: {
          top: 0,
          bottom: 0,
          left: 10,
          right: 10
        },
        position: 'back',
        xaxis: {
          lines: {
            show: false
          }
        },
        yaxis: {
          lines: {
            show: true
          }
        }
      },
      stroke: {
        width: 0,
        curve: 'smooth',
        lineCap: 'round'
      },
      legend: {
        show: true,
        position: 'top',
        horizontalAlign: 'right',
        floating: true,
        offsetY: -8,
        offsetX: -5,
        fontSize: '12px',
        fontWeight: 500,
        labels: {
          colors: isDark ? '#94a3b8' : '#64748b'
        },
        markers: {
          width: 8,
          height: 8,
          radius: 4,
          offsetX: -4
        },
        itemMargin: {
          horizontal: 12
        }
      },
      tooltip: {
        enabled: true,
        shared: true,
        intersect: false,
        followCursor: false,
        theme: isDark ? 'dark' : 'light',
        style: {
          fontSize: '13px',
          fontFamily: 'Inter, system-ui, sans-serif'
        },
        x: {
          show: true,
          formatter: (value: number): string => { 
            const format = this.viewMode === 'daily' ? "d MMMM yyyy" : "LLLL yyyy"; 
            return DateTime.fromMillis(value).setLocale('pl').toFormat(format); 
          }
        },
        y: {
          formatter: (value: number, { seriesIndex, w }): string => { 
            if (typeof value === 'undefined') return '';
            const seriesName = w?.config?.series?.[seriesIndex]?.name || '';
            const currencyMatch = seriesName.match(/\s([A-Z]{3})$/);
            const currency = currencyMatch ? currencyMatch[1] : 'PLN';
            return `${value.toLocaleString('pl-PL', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${currency}`;
          }
        },
        marker: {
          show: true
        }
      },
      xaxis: {
        type: 'datetime',
        axisBorder: {
          show: false
        },
        axisTicks: {
          show: false
        },
        crosshairs: {
          show: true,
          stroke: {
            color: isDark ? '#475569' : '#cbd5e1',
            dashArray: 4,
            width: 1
          }
        },
        labels: {
          show: true,
          offsetY: 0,
          style: {
            colors: isDark ? '#64748b' : '#94a3b8',
            fontSize: '11px',
            fontWeight: 500
          },
          datetimeFormatter: {
            year: 'yyyy',
            month: "MMM 'yy",
            day: 'dd MMM',
            hour: 'HH:mm'
          }
        },
        tickAmount: 6,
        tooltip: {
          enabled: false
        }
      },
      yaxis: {
        show: true,
        axisTicks: {
          show: false
        },
        axisBorder: {
          show: false
        },
        min: (min): number => min < 0 ? min + (min * 0.1) : Math.max(0, min - (min * 0.1)),
        max: (max): number => max + (max * 0.1),
        tickAmount: 4,
        labels: {
          show: true,
          offsetX: -10,
          style: {
            colors: isDark ? '#64748b' : '#94a3b8',
            fontSize: '11px',
            fontWeight: 500
          },
          formatter: (value: number): string => {
            if (value >= 1000000) return `${(value / 1000000).toFixed(1)}M`;
            if (value >= 1000) return `${(value / 1000).toFixed(0)}k`;
            return value.toFixed(0);
          }
        }
      },
      markers: {
        size: 0
      },
      plotOptions: {
        bar: {
          horizontal: false,
          columnWidth: '60%',
          borderRadius: 4,
          borderRadiusApplication: 'end'
        }
      }
    };
  }

  ngOnDestroy(): void {
    this._subscriptions.forEach(sub => sub.unsubscribe())
  }

  ngOnInit() {
    this._subscriptions.push(
      this._schemeService.currentScheme.subscribe({
        next: (scheme) => {
          this.scheme = scheme
          this._prepareChartOptions()
          this.setThisMonthRange()
        }
      })
    )
  }

  /**
   * Oblicza sumy przychodów i kosztów dla całego wybranego okresu
   */
  private _calculatePeriodSummaries() {
    // Ustaw tekst z opisem wybranego okresu
    const startFormatted = this.startDate.setLocale('pl').toFormat('d MMMM yyyy');
    const endFormatted = this.endDate.setLocale('pl').toFormat('d MMMM yyyy');
    this.visibleDataText = `${startFormatted} — ${endFormatted}`;

    this.summaries = [];
    this.expenseSummaries = [];

    for (const cd of this.chartData) {
      // Suma wszystkich punktów danych w serii
      const totalAmount = cd.data.reduce((sum, point) => sum + (point.y || 0), 0);

      // Wyciągnij walutę z nazwy serii (np. "Przychody PLN" -> "PLN", "Koszty EUR" -> "EUR")
      const currencyMatch = cd.name.match(/\s([A-Z]{3})$/);
      const currency = currencyMatch ? currencyMatch[1] : 'PLN';

      // Pokaż serie z niezerową kwotą, lub zawsze 'Przychody PLN'
      const shouldShow = totalAmount !== 0 || cd.name === 'Przychody PLN';

      if (shouldShow) {
        const item = {
          name: cd.name,
          amount: totalAmount,
          amountDecimal: this.toDecimal(totalAmount),
          category: cd.category,
          currency: currency
        };

        if (cd.category === 'income') {
          this.summaries.push(item);
        } else if (cd.category === 'expense') {
          this.expenseSummaries.push(item);
        }
      }
    }
  }
}